import { AdminQueryError } from '../../../components/admin/AdminQueryError';
import React, { useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Chip,
  FormControl,
  FormLabel,
  IconButton,
  Input,
  LinearProgress,
  Option,
  Select,
  Sheet,
  Textarea,
  Typography,
} from '@mui/joy';
import type { ColorPaletteProp } from '@mui/joy';
import { Plus, ArrowsClockwise } from '@phosphor-icons/react';
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { ResponsiveTable } from '../../../components/common/ResponsiveTable';
import type { ResponsiveColumn } from '../../../components/common/ResponsiveTable';
import { CreateRecordModal } from '../../../components/admin/CreateRecordModal';
import { DetailMetric } from '../../../components/common/DetailDrawer';
import { AdminNpsAPI } from '../../../lib/apiAdmin';
import { AccountSection } from '../accounts/AccountSection';
import { getApiErrorMessage } from '../../../lib/apiError';
import type { NpsCampaign, NpsCategory, NpsResponse } from '../../../types/admin';

const CATEGORY_META: Record<NpsCategory, { label: string; color: ColorPaletteProp }> = {
  promoter: { label: 'Promoter', color: 'success' },
  passive: { label: 'Passive', color: 'neutral' },
  detractor: { label: 'Detractor', color: 'danger' },
};

const formatDate = (value: string | null) =>
  value ? new Date(value).toLocaleDateString(undefined, { month: 'short', day: 'numeric' }) : '—';

/**
 * NPS: the campaign that decides who sees the footer prompt, the score it has
 * produced, and the answers behind it.
 *
 * Detractors are the point of the screen — an unresolved one is a customer
 * telling you they are unhappy — so they are filterable and each carries a
 * resolution note rather than only a tick.
 */
export const AdminNpsPanel: React.FC = () => {
  const queryClient = useQueryClient();
  const [creating, setCreating] = useState(false);
  const [createError, setCreateError] = useState<string | null>(null);
  const [category, setCategory] = useState<string>('');
  const [resolved, setResolved] = useState<string>('');
  const [noteDrafts, setNoteDrafts] = useState<Record<string, string>>({});

  const campaignsQuery = useQuery({
    queryKey: ['admin', 'nps-campaigns'],
    queryFn: () => AdminNpsAPI.listCampaigns(),
  });

  const summaryQuery = useQuery({
    queryKey: ['admin', 'nps-summary'],
    queryFn: () => AdminNpsAPI.summary(),
  });

  const responsesQuery = useQuery({
    queryKey: ['admin', 'nps-responses', { category, resolved }],
    queryFn: () =>
      AdminNpsAPI.listResponses({
        category: category || undefined,
        resolved: resolved || undefined,
        page_size: 1000,
      }),
  });

  const invalidate = () => {
    queryClient.invalidateQueries({ queryKey: ['admin', 'nps-campaigns'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'nps-summary'] });
    queryClient.invalidateQueries({ queryKey: ['admin', 'nps-responses'] });
  };

  const createCampaign = useMutation({
    mutationFn: (values: Record<string, string>) =>
      AdminNpsAPI.createCampaign({
        name: values.name,
        question_text: values.question_text,
        min_days_since_signup: Number(values.min_days_since_signup || 21),
      }),
    onSuccess: () => {
      setCreating(false);
      setCreateError(null);
      invalidate();
    },
    onError: (error: unknown) =>
      setCreateError(getApiErrorMessage(error, 'That campaign could not be created.')),
  });

  const toggleCampaign = useMutation({
    mutationFn: ({ id, activate }: { id: string; activate: boolean }) =>
      activate ? AdminNpsAPI.activateCampaign(id) : AdminNpsAPI.deactivateCampaign(id),
    onSuccess: invalidate,
  });

  const updateCampaign = useMutation({
    mutationFn: ({ id, changes }: { id: string; changes: Partial<NpsCampaign> }) =>
      AdminNpsAPI.updateCampaign(id, changes),
    onSuccess: invalidate,
  });

  const resolveResponse = useMutation({
    mutationFn: ({ id, changes }: { id: string; changes: Partial<NpsResponse> }) =>
      AdminNpsAPI.updateResponse(id, changes),
    onSuccess: (_data, variables) => {
      setNoteDrafts((prev) => {
        const next = { ...prev };
        delete next[variables.id];
        return next;
      });
      invalidate();
    },
  });

  const summary = summaryQuery.data;
  const campaigns = campaignsQuery.data?.results ?? [];
  const live = campaigns.find((campaign) => campaign.is_active);

  const responseColumns: ResponsiveColumn<NpsResponse>[] = [
    {
      key: 'score',
      header: 'Score',
      width: '10%',
      mobileVariant: 'header',
      mobileLabel: 'Score',
      sortValue: (row) => row.score,
      render: (row) => (
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Typography level="title-md">{row.score}</Typography>
          <Chip size="sm" variant="soft" color={CATEGORY_META[row.category].color}>
            {CATEGORY_META[row.category].label}
          </Chip>
        </Box>
      ),
    },
    {
      key: 'organization',
      header: 'Customer',
      width: '22%',
      sortValue: (row) => row.organization_name,
      render: (row) => (
        <Box>
          <Typography level="body-sm">{row.organization_name || '—'}</Typography>
          {row.respondent && (
            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
              {row.respondent}
            </Typography>
          )}
        </Box>
      ),
    },
    {
      key: 'comment',
      header: 'What they said',
      mobileVariant: 'body',
      render: (row) =>
        row.comment ? (
          <Typography level="body-sm">{row.comment}</Typography>
        ) : (
          <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
            Score only
          </Typography>
        ),
    },
    {
      key: 'submitted',
      header: 'When',
      width: '12%',
      sortValue: (row) => new Date(row.submitted_at),
      render: (row) => <Typography level="body-xs">{formatDate(row.submitted_at)}</Typography>,
    },
    {
      key: 'resolved',
      header: 'Followed up',
      width: '20%',
      // The follow-up box is a textarea and a button; on a card it needs the
      // full width rather than the right-hand half of a label/value row.
      mobileVariant: 'body',
      mobileLabel: 'Followed up',
      // Unresolved first on the first click — that is the actionable end.
      sortValue: (row) => row.resolved,
      render: (row) => {
        if (row.resolved) {
          return (
            <Box>
              <Chip size="sm" variant="soft" color="success">
                Resolved {formatDate(row.resolved_at)}
              </Chip>
              {row.resolution_notes && (
                <Typography level="body-xs" sx={{ color: 'text.tertiary', mt: 0.5 }}>
                  {row.resolution_notes}
                </Typography>
              )}
            </Box>
          );
        }
        // Only detractors get a follow-up box: a promoter needs no chasing, and
        // offering the control everywhere buries the ones that matter.
        if (row.category !== 'detractor') {
          return (
            <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
              —
            </Typography>
          );
        }
        return (
          <Box sx={{ display: 'grid', gap: 0.5, minWidth: { xs: 0, sm: 180 } }}>
            <Textarea
              size="sm"
              minRows={1}
              placeholder="What you did about it"
              value={noteDrafts[row.id] ?? ''}
              onChange={(event) => setNoteDrafts((prev) => ({ ...prev, [row.id]: event.target.value }))}
            />
            <Button
              size="sm"
              variant="outlined"
              loading={resolveResponse.isPending && resolveResponse.variables?.id === row.id}
              onClick={() =>
                resolveResponse.mutate({
                  id: row.id,
                  changes: { resolved: true, resolution_notes: noteDrafts[row.id] ?? '' },
                })
              }
            >
              Mark resolved
            </Button>
          </Box>
        );
      },
    },
  ];

  return (
    <Box sx={{ display: 'grid', gap: 3 }}>
      <Box>
        <Typography component="h2" level="h3">
          Customer surveys
        </Typography>
        <Typography level="body-sm" sx={{ mt: 0.5 }}>
          Ask how likely customers are to recommend ApexOS, then follow up on their feedback.
        </Typography>
      </Box>
      <AdminQueryError
        error={campaignsQuery.error || summaryQuery.error || responsesQuery.error}
        retry={invalidate}
      />
      {(toggleCampaign.error || updateCampaign.error || resolveResponse.error) && (
        <Alert color="danger">
          {getApiErrorMessage(
            toggleCampaign.error || updateCampaign.error || resolveResponse.error,
            'The update could not be saved.',
          )}
        </Alert>
      )}
      <CreateRecordModal
        open={creating}
        onClose={() => {
          setCreating(false);
          setCreateError(null);
        }}
        title="New NPS campaign"
        submitLabel="Create"
        isSubmitting={createCampaign.isPending}
        error={createError}
        fields={[
          { name: 'name', label: 'Name', required: true, placeholder: 'Three weeks in' },
          {
            name: 'question_text',
            label: 'Question',
            type: 'textarea',
            required: true,
            placeholder: 'How likely are you to recommend ApexOS to another contractor?',
          },
          {
            name: 'min_days_since_signup',
            label: 'Ask after (days)',
            placeholder: '21',
          },
        ]}
        onSubmit={(values) => createCampaign.mutate(values)}
      />

      <AccountSection
        title="Customer sentiment"
        description="Net Promoter Score (NPS) summarizes 0–10 recommendation ratings. Use the responses below to understand what needs attention."
        help="Promoters score 9–10, passives 7–8, and detractors 0–6. NPS is the percentage of promoters minus the percentage of detractors, from −100 to +100. Unresolved counts detractors who still need follow-up."
      >
        <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap', alignItems: 'center' }}>
          <DetailMetric label="NPS" value={summary?.score ?? (summaryQuery.isLoading ? '…' : '—')} />
          <DetailMetric label="Responses" value={summary?.total ?? 0} />
          <DetailMetric label="Promoters" value={summary?.promoters ?? 0} />
          <DetailMetric label="Passives" value={summary?.passives ?? 0} />
          <DetailMetric label="Detractors" value={summary?.detractors ?? 0} />
          <DetailMetric label="Unresolved" value={summary?.unresolved_detractors ?? 0} />
        </Box>
      </AccountSection>
      {!live && !campaignsQuery.isLoading && (
        <Alert color="warning" variant="soft">
          No campaign is live, so nobody is being asked. Activate one below.
        </Alert>
      )}

      <AccountSection
        title="Survey campaigns"
        description="Choose the question and who sees it in ApexOS. Make a campaign live to start asking eligible customers."
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
          <Box sx={{ flex: 1 }} />
          <Button
            size="sm"
            variant="outlined"
            startDecorator={<Plus size={16} />}
            onClick={() => setCreating(true)}
          >
            New campaign
          </Button>
          <IconButton size="sm" variant="outlined" color="neutral" aria-label="Refresh" onClick={invalidate}>
            <ArrowsClockwise size={16} />
          </IconButton>
        </Box>

        {campaigns.map((campaign) => (
          <Sheet
            key={campaign.id}
            variant="outlined"
            sx={{ p: 2, borderRadius: 'md', display: 'grid', gap: 1.5 }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
              <Typography level="title-sm">{campaign.name}</Typography>
              <Chip size="sm" variant="soft" color={campaign.is_active ? 'success' : 'neutral'}>
                {campaign.is_active ? 'Live' : 'Off'}
              </Chip>
              <Typography level="body-xs" sx={{ color: 'text.tertiary' }}>
                {campaign.response_count} response{campaign.response_count === 1 ? '' : 's'}
              </Typography>
              <Box sx={{ flex: 1 }} />
              <Button
                size="sm"
                variant={campaign.is_active ? 'plain' : 'solid'}
                color={campaign.is_active ? 'neutral' : 'primary'}
                loading={toggleCampaign.isPending && toggleCampaign.variables?.id === campaign.id}
                onClick={() => toggleCampaign.mutate({ id: campaign.id, activate: !campaign.is_active })}
              >
                {campaign.is_active ? 'Turn off' : 'Make live'}
              </Button>
            </Box>

            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              {campaign.question_text}
            </Typography>

            <Box sx={{ display: 'flex', gap: 1, flexWrap: 'wrap' }}>
              <FormControl sx={{ width: { xs: '100%', sm: 130 } }}>
                <FormLabel>Ask after (days)</FormLabel>
                <Input
                  size="sm"
                  type="number"
                  slotProps={{ input: { min: 0 } }}
                  defaultValue={campaign.min_days_since_signup}
                  onBlur={(event) =>
                    Number(event.target.value) !== campaign.min_days_since_signup &&
                    updateCampaign.mutate({
                      id: campaign.id,
                      changes: { min_days_since_signup: Number(event.target.value) },
                    })
                  }
                />
              </FormControl>
              <FormControl sx={{ width: { xs: '100%', sm: 130 } }}>
                <FormLabel>Ask again after (days)</FormLabel>
                <Input
                  size="sm"
                  type="number"
                  slotProps={{ input: { min: 1 } }}
                  defaultValue={campaign.response_cooldown_days}
                  onBlur={(event) =>
                    Number(event.target.value) !== campaign.response_cooldown_days &&
                    updateCampaign.mutate({
                      id: campaign.id,
                      changes: { response_cooldown_days: Number(event.target.value) },
                    })
                  }
                />
              </FormControl>
              <FormControl sx={{ width: { xs: '100%', sm: 150 } }}>
                <FormLabel>People per customer</FormLabel>
                <Input
                  size="sm"
                  type="number"
                  slotProps={{ input: { min: 1 } }}
                  defaultValue={campaign.max_prompted_users_per_org}
                  onBlur={(event) =>
                    Number(event.target.value) !== campaign.max_prompted_users_per_org &&
                    updateCampaign.mutate({
                      id: campaign.id,
                      changes: { max_prompted_users_per_org: Number(event.target.value) },
                    })
                  }
                />
              </FormControl>
            </Box>
            <Typography level="body-xs">Timing and people limits save when you leave each field.</Typography>
          </Sheet>
        ))}

        {campaigns.length === 0 && !campaignsQuery.isLoading && (
          <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
            No campaigns yet.
          </Typography>
        )}
      </AccountSection>

      <AccountSection
        title="Responses & follow-up"
        description="Read customer comments and document how you helped. Filter to detractors who have not been followed up with to prioritize outreach."
      >
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, flexWrap: 'wrap' }}>
          <Box sx={{ flex: 1 }} />
          <Select
            size="sm"
            value={category}
            onChange={(_, value) => setCategory((value as string) ?? '')}
            sx={{ minWidth: { xs: 0, sm: 140 }, flex: { xs: '1 1 45%', sm: '0 0 auto' } }}
          >
            <Option value="">Everyone</Option>
            <Option value="promoter">Promoters</Option>
            <Option value="passive">Passives</Option>
            <Option value="detractor">Detractors</Option>
          </Select>
          <Select
            size="sm"
            value={resolved}
            onChange={(_, value) => setResolved((value as string) ?? '')}
            sx={{ minWidth: { xs: 0, sm: 150 }, flex: { xs: '1 1 45%', sm: '0 0 auto' } }}
          >
            <Option value="">Followed up or not</Option>
            <Option value="false">Not followed up</Option>
            <Option value="true">Followed up</Option>
          </Select>
        </Box>

        {responsesQuery.isFetching && <LinearProgress size="sm" />}

        <ResponsiveTable<NpsResponse>
          rows={responsesQuery.data?.results ?? []}
          getRowKey={(row) => row.id}
          columns={responseColumns}
          pagination={{ pageSize: 25, itemNoun: 'responses' }}
          emptyState={
            <Typography level="body-sm" sx={{ color: 'text.secondary' }}>
              No responses yet. They arrive from the footer prompt in the product.
            </Typography>
          }
        />
      </AccountSection>
    </Box>
  );
};

export default AdminNpsPanel;
